import React, { useState, useEffect } from 'react';
import { X, Save, Sparkles, Star, AlertTriangle, HeartHandshake, Calendar, ArrowUpCircle, ChevronLeft, ChevronRight, ChevronDown } from 'lucide-react';
import { 
  ALL_SUBSKILLS, NATURES, INGREDIENT_ICONS, MAIN_SKILL_ICONS, BERRY_ICONS,
  formatSkillName, getSkillColor, HYBRID_ROLES 
} from '../utils/gameData';
import { getEffectiveSkillLevel, getToxelEvolution, calculateHelpTime } from '../utils/pokemonMaths';

const formatFreq = (freq) => {
  if (freq === null || freq === undefined) return '--';
  let seconds = 0;
  if (typeof freq === 'number') seconds = freq;
  else if (typeof freq === 'string' && freq.includes(',')) {
      const p = freq.split(',').map(Number);
      seconds = (p[0] * 3600) + (p[1] * 60) + (p[2] || 0);
  } else {
      seconds = Number(freq) || 3600;
  }
  // Round to nearest second to prevent long decimal places from float calculations
  seconds = Math.round(seconds);
  
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  const s = seconds % 60;
  let str = '';
  if (h > 0) str += `${h}h `;
  if (m > 0) str += `${m}m `;
  if (s > 0 || str === '') str += `${s}s`;
  return str.trim();
};

export default function EditModal({ mon, pokedex, onClose, onSave, onNext, onPrev }) {
  const [editedMon, setEditedMon] = useState({ ...mon });
  
  const species = pokedex[editedMon.species_id] || {};
  const [hybridRole, setHybridRole] = useState(null);
  const [hasHelpingBonus, setHasHelpingBonus] = useState(false);

  const [touchStartX, setTouchStartX] = useState(null);
  const [touchEndX, setTouchEndX] = useState(null);

  const checkHasChanges = () => {
      if (mon.species_id !== editedMon.species_id) return true;
      if (mon.nickname !== editedMon.nickname) return true;
      if (mon.level !== editedMon.level) return true;
      if (mon.nature !== editedMon.nature) return true;
      if (mon.custom_skill !== editedMon.custom_skill) return true;
      if (mon.main_skill_level !== editedMon.main_skill_level) return true;
      if (mon.is_shiny !== editedMon.is_shiny) return true;
      if (mon.is_favorite !== editedMon.is_favorite) return true;
      if (mon.date_met !== editedMon.date_met) return true;
      for (let i = 0; i < 5; i++) {
          if (mon.subskills[i] !== editedMon.subskills[i]) return true;
      }
      for (const lvl of ["0", "30", "60"]) {
          if (mon.ingredients?.[lvl]?.name !== editedMon.ingredients?.[lvl]?.name) return true;
          if (mon.ingredients?.[lvl]?.count !== editedMon.ingredients?.[lvl]?.count) return true;
      }
      return false;
  };

  const handleNavigation = async (navigateFn) => {
      if (checkHasChanges()) {
          const save = window.confirm("You have unsaved changes.\n\nClick OK to save your changes and continue to the next Pokémon.\nClick Cancel to stay here.");
          if (save) {
              await onSave(editedMon, false);
              navigateFn();
          }
      } else {
          navigateFn();
      }
  };

  useEffect(() => {
    setHybridRole(HYBRID_ROLES[editedMon.species_id]);
    const unlockLevels = [10, 25, 50, 75, 100];
    const hbActive = editedMon.subskills.some((s, i) => s === 'Helping Bonus' && editedMon.level >= unlockLevels[i]);
    setHasHelpingBonus(hbActive);
  }, [editedMon.species_id, editedMon.subskills, editedMon.level]);

  // Keyboard & Swipe Navigation
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape') onClose();
      
      // Prevent hijacking arrow keys if typing in the nickname input
      if (e.target.closest('input, select, textarea')) return;
      if (e.key === 'ArrowLeft' && onPrev) handleNavigation(onPrev);
      if (e.key === 'ArrowRight' && onNext) handleNavigation(onNext);
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [onClose, onNext, onPrev, editedMon, mon]);

  const onTouchStart = (e) => {
    // Prevent swiping when interacting with inputs to not hijack sliders
    if (e.target.closest('input, select, button')) return;
    setTouchEndX(null);
    setTouchStartX(e.targetTouches[0].clientX);
  };

  const onTouchMove = (e) => setTouchEndX(e.targetTouches[0].clientX);

  const handleTouchEnd = () => {
    if (!touchStartX || !touchEndX) return;
    const distance = touchStartX - touchEndX;
    if (distance > 50 && onNext) handleNavigation(onNext);
    if (distance < -50 && onPrev) handleNavigation(onPrev);
  };

  const handleChange = (field, value) => {
    setEditedMon(prev => ({ ...prev, [field]: value }));
  };

  const handleSpeciesChange = (newId) => {
    const newSpecies = pokedex[newId];
    setEditedMon(prev => ({
      ...prev,
      species_id: newId,
      specialty: newSpecies.specialty,
      berry: newSpecies.berry,
      carry_size: newSpecies.carrySize,
      frequency: newSpecies.frequency,
      ingredient_percentage: newSpecies.ingredientPercentage,
      ingredients: {
        "0": newSpecies.ingredientPools["0"][0],
        "30": newSpecies.ingredientPools["30"][0],
        "60": newSpecies.ingredientPools["60"][0]
      }
    }));
  };

  const handleEvolve = (targetId) => {
    const newSpecies = pokedex[targetId];
    if (!newSpecies) return;

    // Match existing ingredients to the new species pools to keep the selected path but update yields
    const newIngredients = { ...editedMon.ingredients };
    ["0", "30", "60"].forEach(lvl => {
        const currentName = editedMon.ingredients[lvl]?.name;
        const newPool = newSpecies.ingredientPools[lvl] || [];
        // Find the ingredient with the same name, fallback to first if not found
        const matchedIng = newPool.find(i => i.name === currentName) || newPool[0];
        newIngredients[lvl] = matchedIng;
    });

    setEditedMon(prev => ({
        ...prev,
        species_id: targetId,
        specialty: newSpecies.specialty,
        berry: newSpecies.berry,
        carry_size: newSpecies.carrySize,
        frequency: newSpecies.frequency,
        ingredient_percentage: newSpecies.ingredientPercentage,
        ingredients: newIngredients,
        // Increment skill level (assuming current base cap is 6, or 7 for BulkUp)
        main_skill_level: Math.min((prev.main_skill_level || 1) + 1, newSpecies.skill === 'BulkUp' ? 7 : 6)
    }));
  };

  const handleSubskillChange = (index, value) => {
    const newSkills = [...editedMon.subskills];
    newSkills[index] = value;
    setEditedMon(prev => ({ ...prev, subskills: newSkills }));
  };

  const handleIngredientChange = (level, ingName) => {
    const pool = species.ingredientPools[level];
    const target = pool.find(i => i.name === ingName) || pool[0];
    setEditedMon(prev => ({
      ...prev,
      ingredients: { ...prev.ingredients, [level]: target }
    }));
  };

  // Cycle through ingredients on click
  const cycleIngredient = (level) => {
    const pool = species.ingredientPools[level];
    if (!pool || pool.length <= 1) return; // Nothing to cycle

    const currentName = editedMon.ingredients[level].name;
    const currentIndex = pool.findIndex(i => i.name === currentName);
    const nextIndex = (currentIndex + 1) % pool.length;
    const nextIng = pool[nextIndex];

    handleIngredientChange(level, nextIng.name);
  };

  const sortedSpecies = Object.values(pokedex).sort((a, b) => a.name.localeCompare(b.name));
  const mainSkillIcon = MAIN_SKILL_ICONS[species.skill] || "/images/mainskill/metronome.png";

  // Calculate active subskills
  const unlockLevels = [10, 25, 50, 75, 100];
  const activeSubskills = editedMon.subskills.filter((skill, i) => skill && editedMon.level >= unlockLevels[i]);

  // Calculate the dynamic frequency
  const { finalFreq } = calculateHelpTime(
    species.frequency,
    editedMon.level,
    activeSubskills,
    editedMon.nature || 'Serious',
    0
  );
  const calculatedFrequency = finalFreq;

  // Calculate dynamic berry count
  let berryCount = (species.specialty === 'berry' || species.specialty === 'all') ? 2 : 1;
  if (activeSubskills.includes('Berry Finding S')) {
      berryCount += 1;
  }

  return (
    <div 
      className="fixed inset-0 bg-gray-900/60 backdrop-blur-sm z-50 flex items-center justify-center p-4 animate-in fade-in duration-200"
      onTouchStart={onTouchStart}
      onTouchMove={onTouchMove}
      onTouchEnd={handleTouchEnd}
      onClick={onClose}
    >
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg max-h-[90vh] overflow-hidden flex flex-col ring-1 ring-gray-900/5" onClick={(e) => e.stopPropagation()}>
        
        <div className="bg-gray-800 p-3 md:p-4 flex justify-between items-start shrink-0 shadow-md z-20 text-white">
          <div className="flex items-center gap-2 mb-1">
              <h2 className="text-lg md:text-xl font-black text-white tracking-tight">Edit Pokémon</h2>
          </div>
          <div className="flex items-center gap-2">
            {onPrev && onNext && (
              <div className="flex bg-gray-700 rounded-lg shadow-sm">
                <button onClick={() => handleNavigation(onPrev)} className="p-1.5 md:p-2 hover:bg-gray-600 rounded-l-lg transition-colors text-gray-300 hover:text-white" title="Previous Pokémon (Left Arrow)"><ChevronLeft size={20} /></button>
                <div className="w-px bg-gray-600"></div>
                <button onClick={() => handleNavigation(onNext)} className="p-1.5 md:p-2 hover:bg-gray-600 rounded-r-lg transition-colors text-gray-300 hover:text-white" title="Next Pokémon (Right Arrow)"><ChevronRight size={20} /></button>
              </div>
            )}
            <button onClick={onClose} className="p-1.5 md:p-2 bg-gray-700 hover:bg-red-500 rounded-lg transition-colors text-gray-300 hover:text-white shadow-sm"><X size={20} /></button>
          </div>
        </div>

        <div className="overflow-y-auto custom-scrollbar flex-1 bg-gray-100 pb-6">
          
          {/* Top Profile Section */}
          <div className="bg-white p-3 md:p-4 border-b border-gray-200 shadow-sm flex flex-col gap-3">
              <div className="grid grid-cols-[auto,1fr] gap-3">
                  {/* Left Column: Image & Overlay Toggles */}
                  <div className="relative w-24 h-24 md:w-28 md:h-28 shrink-0">
                      <div className="absolute top-0 left-0 flex flex-col gap-1.5 z-20">
                          <button 
                              onClick={() => handleChange('is_shiny', !editedMon.is_shiny)}
                              className={`p-1.5 rounded-full border shadow-sm transition-colors ${editedMon.is_shiny ? 'bg-indigo-500 border-indigo-400 text-white' : 'bg-white/80 backdrop-blur-sm border-gray-200 text-gray-400 hover:text-indigo-500'}`}
                              title="Toggle Shiny"
                          >
                              <Sparkles size={12} className={editedMon.is_shiny ? "fill-white" : ""} />
                          </button>
                          <button 
                              onClick={() => handleChange('is_favorite', !editedMon.is_favorite)}
                              className={`p-1.5 rounded-full border shadow-sm transition-colors ${editedMon.is_favorite ? 'bg-yellow-50 border-yellow-300 text-yellow-400' : 'bg-white/80 backdrop-blur-sm border-gray-200 text-gray-400 hover:text-yellow-400'}`}
                              title="Toggle Favorite"
                          >
                              <Star size={12} className={editedMon.is_favorite ? "fill-yellow-400" : ""} />
                          </button>
                      </div>
                      <img 
                          src={`/images/pokemon/${editedMon.species_id}${editedMon.is_shiny ? '_shiny' : ''}.png`} 
                          className="w-full h-full object-contain drop-shadow-xl relative z-10" 
                          onError={(e) => {e.target.onerror = null; e.target.src="/images/pokemon/unknown.png"}}
                          alt="" 
                      />
                  </div>

                  {/* Right Column: Name, Species, Level, Evolution */}
                  <div className="flex flex-col gap-2 min-w-0 justify-center">
                      <div className="flex flex-col gap-1.5">
                          <input 
                              type="text" 
                              className="w-full h-[36px] px-3 bg-gray-50 border border-gray-200 rounded-lg font-bold text-gray-700 focus:ring-2 focus:ring-indigo-500 outline-none text-sm shadow-inner"
                              value={editedMon.nickname}
                              placeholder={species.name}
                              onChange={(e) => handleChange('nickname', e.target.value)}
                          />
                          <select 
                              className="w-full h-[36px] px-3 bg-white border border-gray-200 rounded-lg font-bold text-gray-500 focus:ring-2 focus:ring-indigo-500 outline-none text-sm shadow-sm cursor-pointer"
                              value={editedMon.species_id}
                              onChange={(e) => handleSpeciesChange(e.target.value)}
                          >
                              {sortedSpecies.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
                          </select>
                      </div>

                      <div className="flex flex-row items-center gap-2 mt-1">
                          <div className="flex items-center bg-indigo-50 text-indigo-700 h-[28px] rounded-md border border-indigo-200 shadow-sm shrink-0 overflow-hidden">
                              <button 
                                  type="button" 
                                  onClick={() => handleChange('level', Math.max(1, (editedMon.level || 1) - 1))} 
                                  className="w-6 h-full flex items-center justify-center text-indigo-400 hover:text-indigo-600 hover:bg-indigo-100 transition-colors font-black"
                              >
                                  -
                              </button>
                              <div className="flex items-center justify-center px-1.5 border-x border-indigo-200/50 h-full bg-white min-w-[48px]">
                                  <span className="text-[9px] text-indigo-400 tracking-wider font-black mr-0.5">LV.</span>
                                  <input 
                                      type="number" 
                                      min="1" 
                                      max="100" 
                                      className="bg-transparent w-5 outline-none text-xs font-black text-center p-0 m-0 leading-none [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none" 
                                      value={editedMon.level} 
                                      onChange={(e) => handleChange('level', parseInt(e.target.value) || '')}
                                      onBlur={(e) => { if (!e.target.value || e.target.value < 1) handleChange('level', 1); }}
                                  />
                              </div>
                              <button 
                                  type="button" 
                                  onClick={() => handleChange('level', Math.min(100, (editedMon.level || 1) + 1))} 
                                  className="w-6 h-full flex items-center justify-center text-indigo-400 hover:text-indigo-600 hover:bg-indigo-100 transition-colors font-black"
                              >
                                  +
                              </button>
                          </div>

                          {/* Evolution */}
                          {species.evolves_to && species.evolves_to.length > 0 && (() => {
                              const validEvos = species.evolves_to.filter(evo => {
                                  if (species.id === 'toxel') {
                                      const expectedEvo = getToxelEvolution(editedMon.nature || 'Serious');
                                      if (evo.id !== expectedEvo) return false;
                                  }
                                  return true;
                              });

                              if (validEvos.length === 0) return null;

                              if (validEvos.length > 1) {
                                  return (
                                      <div className="flex-1 min-w-0 relative h-[28px] max-w-[140px]">
                                          <select 
                                              className="appearance-none w-full pl-2.5 pr-6 h-full bg-indigo-50 hover:bg-indigo-100 text-indigo-700 border border-indigo-200 rounded-md text-[10px] font-black outline-none focus:ring-2 focus:ring-indigo-500 shadow-sm cursor-pointer truncate"
                                              onChange={(e) => {
                                                  const evoName = e.target.options[e.target.selectedIndex].text;
                                                  if (e.target.value && window.confirm(`Evolve into ${evoName}? This will update its stats and increase the Main Skill level by 1.`)) {
                                                      handleEvolve(e.target.value);
                                                  }
                                                  e.target.value = ""; // Reset after selection
                                              }}
                                              value=""
                                          >
                                              <option value="" disabled>EVOLVE...</option>
                                              {validEvos.map(evo => (
                                                  <option key={evo.id} value={evo.id}>{evo.name}</option>
                                              ))}
                                          </select>
                                          <ChevronDown size={14} className="absolute right-1.5 top-1/2 -translate-y-1/2 text-indigo-400 pointer-events-none" />
                                      </div>
                                  );
                              } else {
                                  const evo = validEvos[0];
                                  return (
                                      <div className="flex-1 min-w-0 relative h-[28px] max-w-[140px]">
                                          <button
                                              type="button"
                                              onClick={() => { if(window.confirm(`Evolve into ${evo.name}? This will update its stats and increase the Main Skill level by 1.`)) { handleEvolve(evo.id); }}}
                                              className="w-full h-full px-2.5 bg-indigo-50 hover:bg-indigo-100 text-indigo-700 border border-indigo-200 rounded-md text-[10px] font-black transition flex items-center justify-center gap-1.5 shadow-sm truncate"
                                          >
                                              <ArrowUpCircle size={12} className="shrink-0 text-indigo-400" /> {evo.name}
                                          </button>
                                      </div>
                                  );
                              }
                          })()}
                      </div>
                  </div>
              </div>

              {/* Badges Row - Moved below to save vertical space */}
              {(hasHelpingBonus || hybridRole) && (
                  <div className="flex flex-wrap gap-2 w-full pt-1 border-t border-gray-100">
                      {hasHelpingBonus && (
                          <span className="flex items-center gap-1 text-[9px] font-bold px-2 py-1 rounded-md bg-yellow-100 text-yellow-700 border border-yellow-200 shadow-sm" title="This Pokémon grants +5% speed to the team (unlocked)">
                              <HeartHandshake size={10} className="shrink-0" /> TEAM BONUS
                          </span>
                      )}
                      {hybridRole && (
                          <span className="flex items-center gap-1 text-[9px] font-bold px-2 py-1 rounded-md bg-orange-50 text-orange-700 border border-orange-200 shadow-sm" title={hybridRole.desc}>
                              <AlertTriangle size={10} className="shrink-0" /> {hybridRole.label.toUpperCase()}
                          </span>
                      )}
                  </div>
              )}
          </div>

          <div className="p-3 space-y-3">
            {/* Helping Stats */}
            <div className="bg-white rounded-2xl shadow-sm border border-gray-200 overflow-hidden">
                <div className="bg-gray-50 px-3 py-2 border-b border-gray-100 flex items-center justify-between">
                    <span className="text-[11px] font-black uppercase text-gray-500 tracking-widest">Helping Stats</span>
                </div>
                <div className="p-3 flex flex-col gap-3">
                    {/* Horizontal Groupings for Frequency & Berries */}
                    <div className="grid grid-cols-3 gap-2">
                        <div className="bg-gray-50 rounded-xl border border-gray-100 shadow-inner p-2 flex flex-col justify-center items-center h-16">
                            <span className="text-[9px] font-black uppercase text-gray-400 tracking-wider mb-1">Base Freq.</span>
                            <span className="font-bold text-gray-700 text-sm tracking-tight">{formatFreq(species.frequency)}</span>
                        </div>
                        <div className="bg-indigo-50 rounded-xl border border-indigo-100 shadow-inner p-2 flex flex-col justify-center items-center h-16">
                            <span className="text-[9px] font-black uppercase text-indigo-400 tracking-wider mb-1">Calc. Freq.</span>
                            <span className="font-bold text-indigo-700 text-sm tracking-tight">{formatFreq(calculatedFrequency)}</span>
                        </div>
                        <div className="bg-gray-50 rounded-xl border border-gray-100 shadow-inner p-2 flex flex-col justify-center items-center h-16">
                            <span className="text-[9px] font-black uppercase text-gray-400 tracking-wider mb-1">Berries</span>
                            <div className="flex items-center gap-1 bg-white px-2 py-0.5 rounded shadow-sm border border-gray-100" title={species.berry}>
                                <img src={BERRY_ICONS[species.berry]} className="w-4 h-4 object-contain" alt="" />
                                <span className="text-xs font-black text-gray-600">x{berryCount}</span>
                            </div>
                        </div>
                    </div>

                    <div className="space-y-1.5">
                        <span className="text-[9px] font-black uppercase text-gray-400 tracking-wider block">Ingredients (Tap to Cycle)</span>
                        <div className="grid grid-cols-3 gap-2">
                            {["0", "30", "60"].map((lvl) => {
                                const pool = species.ingredientPools?.[lvl] || [];
                                const currentIng = editedMon.ingredients[lvl];
                                const isLocked = editedMon.level < parseInt(lvl);
                                const isMulti = pool.length > 1;

                                return (
                                    <div 
                                        key={lvl} 
                                        className={`relative h-16 py-1.5 px-1 rounded-xl border transition-all select-none flex flex-col items-center justify-center
                                            ${isLocked ? 'bg-gray-50 border-gray-100 opacity-50 hover:opacity-100' : 'bg-white border-indigo-100 shadow-sm'}
                                            ${isMulti ? 'cursor-pointer hover:border-indigo-300 hover:-translate-y-0.5' : 'cursor-default'}
                                        `}
                                        onClick={() => cycleIngredient(lvl)}
                                        title={isMulti ? `Tap to swap: ${pool.map(i => i.name).join(' / ')}` : 'Fixed Ingredient'}
                                    >
                                        <div className="absolute top-1 left-1.5 text-[8px] font-black text-gray-400 leading-none">Lv.{lvl}</div>
                                        <div className="relative mt-2">
                                            <img src={INGREDIENT_ICONS[currentIng.name]} className="w-6 h-6 object-contain drop-shadow-sm" alt="" />
                                            <div className="absolute -bottom-1 -right-1 bg-white border border-gray-200 rounded px-1 text-[8px] font-black text-gray-600 shadow-sm leading-none py-0.5">
                                                x{currentIng.count}
                                            </div>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                </div>
            </div>

            {/* Skills */}
            <div className="bg-white rounded-2xl shadow-sm border border-gray-200 overflow-hidden">
                <div className="bg-gray-50 px-3 py-2 border-b border-gray-100 flex items-center justify-between">
                    <span className="text-[11px] font-black uppercase text-gray-500 tracking-widest">Skills</span>
                </div>
                <div className="p-3 flex flex-col gap-3">
                    {/* Main Skill */}
                    <div className="space-y-2">
                        <label className="text-[9px] font-black uppercase text-gray-400 tracking-wider flex items-center justify-between">
                            Main Skill
                            {editedMon.species_id === 'mew' ? (
                                <select
                                    className="ml-auto text-[10px] font-bold text-indigo-500 bg-gray-50 border border-indigo-100 rounded px-1 py-0.5 outline-none"
                                    value={editedMon.custom_skill || species.skill}
                                    onChange={(e) => handleChange('custom_skill', e.target.value)}
                                >
                                    {Object.keys(MAIN_SKILL_ICONS).sort().map(s => (
                                        <option key={s} value={s}>{formatSkillName(s)}</option>
                                    ))}
                                </select>
                            ) : (
                                <span className="ml-auto text-[10px] font-bold text-indigo-500">{formatSkillName(species.skill)}</span>
                            )}
                        </label>
                        <div className="flex items-center gap-3 bg-white shadow-inner p-3 rounded-xl border border-gray-100">
                            <img src={MAIN_SKILL_ICONS[editedMon.custom_skill || species.skill] || "/images/mainskill/metronome.png"} className="w-7 h-7 object-contain drop-shadow-sm" alt="" title={formatSkillName(editedMon.custom_skill || species.skill)} />
                            <input
                                type="range" min="1" max={(editedMon.custom_skill || species.skill) === 'BulkUp' ? 7 : 6} step="1"
                                className="flex-grow h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-indigo-600"
                                value={editedMon.main_skill_level || 1}
                                onChange={(e) => handleChange('main_skill_level', parseInt(e.target.value))}
                            />

                            {(() => {
                                const activeSubskills = editedMon.subskills.filter((skill, i) => {
                                    const unlockLevel = [10, 25, 50, 75, 100][i];
                                    return editedMon.level >= unlockLevel && skill;
                                });

                                const baseLvl = editedMon.main_skill_level || 1;
                                const effectiveLvl = getEffectiveSkillLevel(baseLvl, activeSubskills);

                                return (
                                    <div className="flex flex-col items-center justify-center w-12 border-l border-gray-200 pl-3 shrink-0">
                                        <span className={`text-xl font-black leading-none ${effectiveLvl > baseLvl ? 'text-green-500' : 'text-indigo-600'}`}>
                                            {effectiveLvl}
                                        </span>
                                        {effectiveLvl > baseLvl && (
                                            <span className="text-[8px] font-bold text-gray-400 leading-none mt-1 text-center">
                                                (Base {baseLvl})
                                            </span>
                                        )}
                                    </div>
                                );
                            })()}
                        </div>
                    </div>

                    <div className="space-y-2">
                        <label className="text-[9px] font-black uppercase text-gray-400 tracking-wider">Sub Skills</label>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                            {editedMon.subskills.map((skill, i) => {
                                const unlockLevel = [10, 25, 50, 75, 100][i];
                                const isUnlocked = editedMon.level >= unlockLevel;
                                const colorClass = getSkillColor(skill);
                                const availableSubskills = ALL_SUBSKILLS.filter(s => !editedMon.subskills.includes(s) || s === skill);

                                return (
                                    <div key={i} className="relative flex items-stretch h-10 shadow-sm rounded-xl overflow-hidden border border-gray-200">
                                        <div className={`w-10 flex flex-col items-center justify-center text-[9px] font-black border-r border-gray-200 shrink-0 ${isUnlocked ? 'bg-gray-100 text-gray-500' : 'bg-gray-50 text-gray-300'}`}>
                                            <span className="text-[8px] font-bold leading-none mb-0.5">Lv.</span>
                                            <span className="leading-none">{unlockLevel}</span>
                                        </div>
                                        <select
                                            className={`flex-1 px-3 text-xs font-bold outline-none cursor-pointer appearance-none ${!skill ? 'bg-white text-gray-400' : isUnlocked ? `${colorClass}` : `${colorClass} opacity-60`}`}
                                            value={skill}
                                            onChange={(e) => handleSubskillChange(i, e.target.value)}
                                        >
                                            <option value="">(Empty Slot)</option>
                                            {availableSubskills.map(s => <option key={s} value={s}>{s}</option>)}
                                        </select>
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                </div>
            </div>

            {/* Additional Stats */}
            <div className="bg-white rounded-2xl shadow-sm border border-gray-200 overflow-hidden">
                <div className="bg-gray-50 px-3 py-2 border-b border-gray-100 flex items-center justify-between">
                    <span className="text-[11px] font-black uppercase text-gray-500 tracking-widest">Additional Stats</span>
                </div>
                <div className="p-3 grid grid-cols-2 gap-3">
                    <div className="space-y-1">
                        <div className="flex justify-between items-center mb-1">
                            <label className="text-[9px] font-black uppercase text-gray-400 tracking-wider">Nature</label>
                        </div>
                        <select
                            className="w-full h-[42px] px-3 bg-gray-50 border border-gray-200 rounded-xl font-bold text-gray-700 outline-none focus:ring-2 focus:ring-indigo-500 text-sm shadow-inner cursor-pointer"
                            value={editedMon.nature}
                            onChange={(e) => handleChange('nature', e.target.value)}
                        >
                            {Object.keys(NATURES).sort().map(n => (
                                <option key={n} value={n}>{n}</option>
                            ))}
                        </select>
                        {editedMon.nature && (
                            <div className="mt-1.5 flex justify-center items-center">
                                <span className="text-[9px] px-2 py-0.5 rounded text-gray-500 font-bold border border-gray-100 bg-gray-50 whitespace-nowrap">
                                    {NATURES[editedMon.nature]?.up ? <><span className="text-red-500">▲ {NATURES[editedMon.nature].up}</span> <span className="text-gray-300 mx-1">|</span> <span className="text-blue-500">▼ {NATURES[editedMon.nature].down}</span></> : 'Neutral'}
                                </span>
                            </div>
                        )}
                    </div>

                    <div className="space-y-1">
                        <div className="flex justify-between items-center mb-1">
                            <label className="text-[9px] font-black uppercase text-gray-400 tracking-wider">Date Met</label>
                            <span className="text-[10px] font-bold text-gray-500">
                                {new Date((editedMon.date_met || new Date().toISOString().split('T')[0]) + 'T12:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                            </span>
                        </div>
                        <div className="relative">
                            <input
                                type="date"
                                className="w-full h-[42px] pl-3 pr-8 bg-gray-50 border border-gray-200 rounded-xl font-bold text-gray-700 focus:ring-2 focus:ring-indigo-500 outline-none text-xs shadow-inner cursor-pointer"
                                value={editedMon.date_met || new Date().toISOString().split('T')[0]}
                                onChange={(e) => handleChange('date_met', e.target.value)}
                            />
                            <Calendar size={14} className="absolute right-3 top-3.5 text-gray-400 pointer-events-none" />
                        </div>
                    </div>
                </div>
            </div>
          </div>
          </div>

          {/* Footer Actions */}
          <div className="p-3 bg-white border-t border-gray-200 shrink-0">
            <div className="flex gap-3">
                <button onClick={onClose} className="flex-1 py-2.5 rounded-xl font-bold text-gray-500 hover:bg-gray-100 transition text-sm">Cancel</button>
                <button onClick={() => onSave(editedMon)} className="flex-[2] py-2.5 rounded-xl font-bold text-white bg-indigo-600 hover:bg-indigo-700 shadow-lg hover:shadow-indigo-500/30 transition flex items-center justify-center gap-2 text-sm">
                    <Save size={18} /> Save Changes
                </button>
            </div>
          </div>
        </div>
      </div>
  );
}